import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Layout from "./layout";
import { campaignAPI } from "../services/api";

const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const OrganizationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const OrganizationCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: #00B5B8;
  }
`;

const LogoContainer = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
  padding: 1rem;
  position: relative;
`;

const LogoPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00B5B8 0%, #00979A 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  font-weight: 600;
  box-shadow: 0 4px 6px rgba(0, 181, 184, 0.15);
`;

const LogoImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const OrganizationName = styled.div`
  padding: 1.25rem;
  text-align: center;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  background: #FFFFFF;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #F3F4F6;
  border-top: 4px solid #00B5B8;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: #6B7280;
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6B7280;
`;

const DashboardOrganization = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  // ฟังก์ชันดึงข้อมูล campaigns
  const loadOrganizations = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await campaignAPI.getList(1); // ดึงหน้าแรก

      console.log('API Response:', response);

      if (response && response.result && response.list) {
        // Firebase Storage base URL (ต้องตรงกับ EditCampaignManagementModal)
        const FIREBASE_STORAGE_BASE_URL = 'https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/';

        // กรองเฉพาะ campaigns ที่มีชื่อ (ไม่เป็นค่าว่าง)
        const campaigns = response.list
          .filter((campaign) => campaign.name && campaign.name.trim() !== '' && campaign.name !== '-')
          .map((campaign) => {
            // แปลง Firebase path เป็น URL เต็ม
            let logoUrl = null;
            if (campaign.image && campaign.image.trim() !== '') {
              // ถ้าเป็น full URL อยู่แล้ว ให้คืนค่ากลับไปตรงๆ
              if (campaign.image.startsWith('http://') || campaign.image.startsWith('https://')) {
                logoUrl = campaign.image;
              } else {
                // ต่อ base URL เข้าไป (เหมือนกับ expandToFullURL ใน EditModal)
                logoUrl = FIREBASE_STORAGE_BASE_URL + campaign.image;
              }
            }

            return {
              id: campaign.id,
              name: campaign.name,
              logo: logoUrl,
              caption: campaign.caption || '',
              hr_url: campaign.hr_url || null, // เก็บ hr_url จาก API
            };
          });

        console.log('Loaded campaigns:', campaigns);
        setOrganizations(campaigns);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setOrganizations([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    // ดึงข้อมูลครั้งแรก
    loadOrganizations();

    // setInterval เพื่อ refresh ทุก 120 วินาที (2 นาที)
    // เนื่องจาก hr_url มีอายุแค่ 120 วินาที
    const intervalId = setInterval(() => {
      console.log('Refreshing campaign list (hr_url expires after 120s)...');
      loadOrganizations(false); // refresh โดยไม่แสดง loading
    }, 120 * 1000); // 120 วินาที

    // Cleanup interval เมื่อ component unmount
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleOrganizationClick = (org) => {
    console.log('Clicked organization:', org);
    // เปิด hr_url ในหน้าต่างใหม่ถ้ามี
    if (org.hr_url) {
      window.open(org.hr_url, '_blank');
    }
  };

  return (
    <Layout>
      <PageContainer>
        <PageTitle>Dashboard องค์กร</PageTitle>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลด...</LoadingText>
          </LoadingContainer>
        ) : organizations.length === 0 ? (
          <EmptyState>
            ไม่พบข้อมูลองค์กร
          </EmptyState>
        ) : (
          <OrganizationGrid>
            {organizations.map((org) => (
              <OrganizationCard
                key={org.id}
                onClick={() => handleOrganizationClick(org)}
              >
                <LogoContainer>
                  {org.logo ? (
                    <LogoImage
                      src={org.logo}
                      alt={org.name}
                    />
                  ) : (
                    <LogoPlaceholder>
                      {org.name.charAt(0).toUpperCase()}
                    </LogoPlaceholder>
                  )}
                </LogoContainer>
                <OrganizationName>{org.name}</OrganizationName>
              </OrganizationCard>
            ))}
          </OrganizationGrid>
        )}
      </PageContainer>
    </Layout>
  );
};

export default DashboardOrganization;
